import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import type { ErpModule } from "@/api/platform.api";
import { searchApi, type GlobalSearchResult } from "@/api/search.api";
import type { NavWorkflowGroup } from "@/components/layout/Sidebar";
import { useDebounce } from "@/hooks/useDebounce";
import { cn } from "@/utils/cn";
import { filterByClientSearch } from "@/utils/clientSearch";
import { resolveBackendLabel, resolveGroupLabel } from "@/i18n/navLabel";

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /** Sidebar workflow groups for the active module — search pages within this module only. */
  navGroups?: NavWorkflowGroup[];
  /** Active ERP module — scopes entity search and empty-state copy. */
  module?: ErpModule | null;
}

interface CommandItem {
  id: string;
  label: string;
  hint?: string;
  to: string;
  kind: "nav" | "entity";
}

export function CommandPalette({
  open,
  onClose,
  navGroups = [],
  module = null,
}: CommandPaletteProps) {
  const { t } = useTranslation(["common", "navigation"]);
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 280);
  const [entityResults, setEntityResults] = useState<GlobalSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const navItems = useMemo(() => {
    const commands: CommandItem[] = [];
    for (const group of navGroups) {
      const groupLabel = group.labelKey
        ? resolveBackendLabel(group.labelKey, group.label, t)
        : resolveGroupLabel(group.id, group.label, t);
      for (const item of group.items ?? []) {
        if (!item.to) continue;
        commands.push({
          id: `nav-${group.id}-${item.to}`,
          label: resolveBackendLabel(item.labelKey, item.label, t),
          hint: groupLabel,
          to: item.to,
          kind: "nav",
        });
      }
    }
    return commands;
  }, [navGroups, t]);

  const filteredNav = useMemo(() => {
    if (!query.trim()) return navItems.slice(0, 16);
    return filterByClientSearch(navItems, query, (command) => [
      command.label,
      command.hint,
      command.to,
    ]);
  }, [navItems, query]);

  const entityItems: CommandItem[] = useMemo(
    () =>
      entityResults.flatMap((result) => {
        if (!result || typeof result.path !== "string" || !result.path) return [];
        const type = String(result.type ?? "result");
        return [{
          id: `${type}-${String(result.id ?? result.path)}`,
          label: String(result.title ?? result.subtitle ?? type),
          hint: result.subtitle
            ? `${type} · ${String(result.subtitle)}`
            : `${type}${result.module ? ` · ${String(result.module)}` : ""}`,
          to: result.path,
          kind: "entity" as const,
        }];
      }),
    [entityResults],
  );

  const items = useMemo(
    () => [...filteredNav, ...entityItems].slice(0, 30),
    [entityItems, filteredNav],
  );

  useEffect(() => {
    if (!open) {
      setQuery("");
      setEntityResults([]);
      setActiveIndex(0);
      return;
    }
    const q = debouncedQuery.trim();
    if (q.length < 2 || !module) {
      setEntityResults([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    searchApi
      .global(q, module)
      .then((results) => {
        if (!cancelled) setEntityResults(Array.isArray(results) ? results : []);
      })
      .catch(() => {
        if (!cancelled) setEntityResults([]);
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, debouncedQuery, module]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, entityResults.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, Math.max(0, items.length - 1)));
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      }
      if (e.key === "Enter" && items[activeIndex]) {
        e.preventDefault();
        navigate(items[activeIndex].to);
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, items, activeIndex, navigate, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center bg-black/50 p-4 pt-[12vh]">
      <div
        className="w-full max-w-xl animate-fade-in overflow-hidden rounded-xl border border-slate-200 bg-white shadow-panel dark:border-slate-700 dark:bg-slate-900"
        role="dialog"
        aria-label={t("commandPalette.ariaLabel")}
      >
        <div className="flex items-center gap-2 border-b border-slate-200 px-3 dark:border-slate-700">
          <SearchIcon className="text-slate-400" fontSize="small" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("commandPalette.placeholderModule")}
            className="flex-1 border-0 bg-transparent py-3 text-sm outline-none dark:text-slate-100"
          />
          <kbd className="hidden rounded border border-slate-200 px-1.5 py-0.5 text-[10px] text-slate-400 sm:inline dark:border-slate-600">
            ESC
          </kbd>
          <button
            type="button"
            onClick={onClose}
            className="workspace-header-icon-btn shrink-0"
            aria-label={t("commandPalette.close")}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <ul className="max-h-80 overflow-y-auto py-1" aria-busy={searching}>
          {items.length === 0 ? (
            <li className="px-4 py-6 text-center text-sm text-slate-500">
              {searching ? t("commandPalette.searching") : t("commandPalette.noMatches")}
            </li>
          ) : (
            items.map((item, index) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={cn(
                    "flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm",
                    index === activeIndex
                      ? "bg-primary-50 text-primary-800 dark:bg-primary-950 dark:text-primary-200"
                      : "text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800",
                  )}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => {
                    navigate(item.to);
                    onClose();
                  }}
                >
                  <span className="min-w-0 truncate">{item.label}</span>
                  <span className="shrink-0 text-xs text-slate-400">{item.hint}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
